(() => {
  'use strict';

  const records = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let mobileClosingTimer = 0;

  function clearMobileClosing() {
    window.clearTimeout(mobileClosingTimer);
    mobileClosingTimer = 0;
    delete document.documentElement.dataset.artboxMenuClosing;
  }

  function markMobileClosing() {
    window.clearTimeout(mobileClosingTimer);
    document.documentElement.dataset.artboxMenuClosing = 'true';
    mobileClosingTimer = window.setTimeout(clearMobileClosing, 260);
  }

  function rootsWithin(scope) {
    if (!scope) return [];
    if (scope.matches?.('[data-artbox-navigation]')) return [scope];
    return Array.from(scope.querySelectorAll?.('[data-artbox-navigation]') || []);
  }

  function emitHeight(root, record) {
    const section = root.closest('.t4s-section-header');
    const hidden = section?.classList.contains('shopify-section-header-hidden') || false;
    const bounds = root.getBoundingClientRect();
    const height = hidden ? 0 : Math.round(bounds.height);
    const bottom = hidden ? 0 : Math.max(0, Math.round(bounds.bottom));

    if (record.height === height && record.bottom === bottom && record.hidden === hidden) return;

    record.height = height;
    record.bottom = bottom;
    record.hidden = hidden;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
    document.documentElement.style.setProperty('--artbox-navigation-bottom', `${bottom}px`);
    document.dispatchEvent(new CustomEvent('artbox:navigation:height', {
      detail: { height, bottom, hidden },
    }));
  }

  function activeDesktopItem(root) {
    const activeItems = root.querySelectorAll('[data-artbox-menu-item].is-action__hover');
    return activeItems[activeItems.length - 1] || null;
  }

  function setOverlayState(root, record, open) {
    const overlay = root.parentElement?.querySelector('[data-artbox-nav-overlay]');
    if (!overlay) return;

    window.clearTimeout(record.overlayTimer);
    cancelAnimationFrame(record.overlayFrame);
    overlay.setAttribute('aria-hidden', String(!open));

    if (open) {
      overlay.hidden = false;
      record.overlayFrame = requestAnimationFrame(() => {
        if (root.dataset.artboxDesktopOpen === 'true') {
          overlay.setAttribute('data-artbox-overlay-visible', 'true');
        }
      });
      return;
    }

    overlay.setAttribute('data-artbox-overlay-visible', 'false');
    const delay = reduceMotion.matches ? 0 : 260;
    record.overlayTimer = window.setTimeout(() => {
      if (root.dataset.artboxDesktopOpen !== 'true') overlay.hidden = true;
    }, delay);
  }

  function preparePanel(panel) {
    const list = panel?.querySelector('.t4s-sub-column');
    if (!list) return;
    const links = Array.from(list.children).filter((item) => item.matches('li'));
    if (!links.length) return;
    let columns = links.length >= 10 ? 3 : links.length >= 6 ? 2 : 1;
    const generations = links.map((item) => Number(item.textContent.trim().match(/^iPhone\s+(\d+)/i)?.[1] || 0));
    const latest = Math.max(...generations);
    const primaryCount = generations.every(Boolean) && latest >= 17
      ? generations.filter((generation) => generation >= latest - 1).length : 0;
    if (primaryCount) columns = 1 + Math.ceil((links.length - primaryCount) / 6);
    list.style.setProperty('--artbox-menu-max-width', columns > 3 ? '980px' : '760px');
    const secondaryRows = primaryCount ? Math.ceil((links.length - primaryCount) / Math.max(1, columns - 1)) : 0;
    const rows = primaryCount ? Math.max(primaryCount, secondaryRows) : Math.ceil(links.length / columns);
    list.style.setProperty('--artbox-menu-columns', String(columns));
    list.style.setProperty('--artbox-menu-rows', String(rows));
    links.forEach((item, index) => {
      const primary = index < (primaryCount || rows);
      item.style.gridColumn = primaryCount ? String(primary ? 1 : 2 + Math.floor((index - primaryCount) / secondaryRows)) : '';
      item.style.gridRow = primaryCount ? String(primary ? index + 1 : (index - primaryCount) % secondaryRows + 1) : '';
      if (primary) item.dataset.artboxMenuPrimary = '';
      else delete item.dataset.artboxMenuPrimary;
    });
  }

  function setSurfaceState(root, record, item, outgoingDrawerHeight = 0) {
    const surface = root.parentElement?.querySelector('[data-artbox-menu-surface]');
    if (!surface) return;
    window.clearTimeout(record.surfaceTimer);

    if (item) {
      delete root.dataset.artboxMenuClosing;
      const panel = item.querySelector('[data-artbox-menu-panel]');
      preparePanel(panel);
      surface.hidden = false;
      const availableHeight = Math.max(0, window.innerHeight - Math.max(0, record.bottom));
      if (outgoingDrawerHeight > 0) {
        surface.style.setProperty('transition', 'none', 'important');
        surface.style.height = `${Math.min(outgoingDrawerHeight, availableHeight)}px`;
        surface.dataset.artboxSurfaceVisible = 'true';
        surface.getBoundingClientRect();
        surface.style.removeProperty('transition');
      }
      surface.style.height = `${Math.min(panel?.scrollHeight || 180, availableHeight) + 1}px`;
      surface.dataset.artboxSurfaceVisible = 'true';
      return;
    }

    surface.dataset.artboxSurfaceVisible = 'false';
    surface.style.height = '0px';
    root.dataset.artboxMenuClosing = 'true';
    record.surfaceTimer = window.setTimeout(() => {
      if (root.dataset.artboxDesktopOpen !== 'true') {
        surface.hidden = true;
        delete root.dataset.artboxMenuClosing;
      }
    }, reduceMotion.matches ? 0 : 260);
  }

  function fadePreviousPanel(record, next) {
    const previous = record.activeItem?.querySelector('[data-artbox-menu-panel]');
    const current = next?.querySelector('[data-artbox-menu-panel]');
    if (previous && previous !== current) {
      previous.dataset.artboxPanelExiting = next ? 'switch' : 'true';
      window.clearTimeout(record.panelTimers.get(previous));
      record.panelTimers.set(previous, window.setTimeout(() => {
        delete previous.dataset.artboxPanelExiting;
        record.panelTimers.delete(previous);
      }, reduceMotion.matches ? 0 : 260));
    }
    if (current) {
      window.clearTimeout(record.panelTimers.get(current));
      record.panelTimers.delete(current);
      delete current.dataset.artboxPanelExiting;
    }
  }

  function syncDesktopState(root, record) {
    if (!record) return;
    cancelAnimationFrame(record.desktopFrame);
    record.desktopFrame = requestAnimationFrame(() => {
      const next = activeDesktopItem(root);
      const outgoingDrawer = next && document.querySelector(
        '#t4s-search-hidden[aria-hidden="false"], #t4s-login-sidebar[aria-hidden="false"], #t4s-mini_cart[aria-hidden="false"]',
      );
      const outgoingDrawerHeight = outgoingDrawer?.getBoundingClientRect().height || 0;

      if (next && (next !== record.activeItem || outgoingDrawer)) {
        document.dispatchEvent(new CustomEvent('artbox:menu:opening'));
      }

      root.querySelectorAll('[data-artbox-menu-item].is-action__hover').forEach((item) => {
        if (item !== next) item.classList.remove('is-action__hover');
      });

      if (next) emitHeight(root, record);
      fadePreviousPanel(record, next);
      record.activeItem = next;
      root.dataset.artboxDesktopOpen = String(Boolean(next));

      if (next) root.dataset.artboxActiveMenu = next.id || '';
      else delete root.dataset.artboxActiveMenu;

      root.querySelectorAll('[data-artbox-menu-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(trigger.closest('[data-artbox-menu-item]') === next));
      });

      setSurfaceState(root, record, next, outgoingDrawerHeight);
      setOverlayState(root, record, Boolean(next));
    });
  }

  function closeDesktopMenu(root, { restoreFocus = false } = {}) {
    const record = records.get(root);
    if (!record) return;
    window.clearTimeout(record.desktopCloseTimer);
    record.desktopCloseTimer = null;
    const trigger = record.activeItem?.querySelector('[data-artbox-menu-trigger]') || record.lastTrigger;
    root.querySelectorAll('[data-artbox-menu-item].is-action__hover').forEach((item) => {
      item.classList.remove('is-action__hover');
    });
    syncDesktopState(root, record);
    if (restoreFocus) trigger?.focus({ preventScroll: true });
  }

  function bindDesktop(root, record) {
    const menu = root.querySelector('[data-menu-nav]');
    if (!menu) return;

    record.desktopObserver = new MutationObserver(() => syncDesktopState(root, record));
    record.desktopObserver.observe(menu, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });

    const onPointerOver = (event) => {
      if (window.innerWidth <= 1024 || event.pointerType === 'touch') return;
      const item = event.target.closest?.('.t4s-nav__ul > li');
      if (!item || item.parentElement !== menu) return;
      window.clearTimeout(record.desktopCloseTimer);
      record.desktopCloseTimer = null;
      if (item.contains(event.relatedTarget)) return;
      if (!item.querySelector('[data-artbox-menu-panel]')) {
        closeDesktopMenu(root);
        return;
      }
      root.querySelectorAll('[data-artbox-menu-item].is-action__hover').forEach((active) => {
        if (active !== item) active.classList.remove('is-action__hover');
      });
      item.classList.add('is-action__hover');
      syncDesktopState(root, record);
    };

    const onPointerOut = (event) => {
      if (window.innerWidth <= 1024 || event.pointerType === 'touch') return;
      const item = event.target.closest?.('[data-artbox-menu-item]');
      if (!item || item.parentElement !== menu || item.contains(event.relatedTarget)) return;
      if (!item.classList.contains('is-action__hover')) return;
      window.clearTimeout(record.desktopCloseTimer);
      record.desktopCloseTimer = window.setTimeout(() => {
        record.desktopCloseTimer = null;
        item.classList.remove('is-action__hover');
        syncDesktopState(root, record);
      }, 150);
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape' && root.dataset.artboxDesktopOpen === 'true') {
        event.preventDefault();
        closeDesktopMenu(root, { restoreFocus: true });
        return;
      }

      const trigger = event.target.closest?.('[data-artbox-menu-trigger]');
      if (!trigger || !root.contains(trigger) || !['Enter', ' '].includes(event.key)) return;

      const item = trigger.closest('[data-artbox-menu-item]');
      const open = item?.classList.contains('is-action__hover');
      record.lastTrigger = trigger;

      if (!open) {
        event.preventDefault();
        root.querySelectorAll('[data-artbox-menu-item].is-action__hover').forEach((active) => {
          active.classList.remove('is-action__hover');
        });
        item?.classList.add('is-action__hover');
        syncDesktopState(root, record);
      } else if (event.key === ' ') {
        event.preventDefault();
        window.location.assign(trigger.href);
      }
    };

    const onOverlayClick = (event) => {
      if (!event.target.closest?.('[data-artbox-nav-overlay]')) return;
      closeDesktopMenu(root);
    };

    const onDrawerClick = (event) => {
      const drawerTrigger = event.target.closest?.('[data-drawer-options]');
      if (drawerTrigger && !root.contains(drawerTrigger)) closeDesktopMenu(root);
    };

    const onResize = () => {
      if (window.innerWidth <= 1024) closeDesktopMenu(root);
      else syncDesktopState(root, record);
    };

    menu.addEventListener('pointerover', onPointerOver);
    menu.addEventListener('pointerout', onPointerOut);
    root.addEventListener('keydown', onKeydown);
    root.parentElement?.addEventListener('click', onOverlayClick);
    document.addEventListener('click', onDrawerClick, true);
    window.addEventListener('resize', onResize);
    record.desktopCleanup = () => {
      menu.removeEventListener('pointerover', onPointerOver);
      menu.removeEventListener('pointerout', onPointerOut);
      window.clearTimeout(record.desktopCloseTimer);
      record.desktopCloseTimer = null;
      root.removeEventListener('keydown', onKeydown);
      root.parentElement?.removeEventListener('click', onOverlayClick);
      document.removeEventListener('click', onDrawerClick, true);
      window.removeEventListener('resize', onResize);
    };

    syncDesktopState(root, record);
  }

  function directSubmenu(item) {
    return Array.from(item.children).find((child) => (
      child.matches('.t4s-sub-menu, .t4s-sub-sub-menu, .t4s-sub-sub-sub-menu')
    )) || null;
  }

  function syncMobileItem(item, record, animate = true) {
    const trigger = Array.from(item.children).find((child) => child.matches('a, button'));
    const submenu = directSubmenu(item);
    if (!trigger || !submenu) return;

    const open = item.classList.contains('is--opend');
    trigger.setAttribute('aria-expanded', String(open));
    const animation = record.mobileAnimations.get(submenu);
    animation?.cancel();

    if (!animate || reduceMotion.matches || typeof submenu.animate !== 'function') return;

    const frames = open
      ? [{ opacity: 0, transform: 'translate3d(0,-6px,0)' }, { opacity: 1, transform: 'translate3d(0,0,0)' }]
      : [{ opacity: 1, transform: 'translate3d(0,0,0)' }, { opacity: 0, transform: 'translate3d(0,-4px,0)' }];
    const nextAnimation = submenu.animate(frames, {
      duration: 220,
      easing: 'cubic-bezier(.22,1,.36,1)',
    });

    record.mobileAnimations.set(submenu, nextAnimation);
    nextAnimation.finished.catch(() => {}).finally(() => {
      if (record.mobileAnimations.get(submenu) === nextAnimation) {
        record.mobileAnimations.delete(submenu);
      }
    });
  }

  function bindMobile(record) {
    const mobileRoot = document.querySelector('[data-artbox-mobile-menu]');
    if (!mobileRoot || record.mobileRoot === mobileRoot) return;

    record.mobileObserver?.disconnect();
    record.mobileDrawerObserver?.disconnect();
    record.mobileDrawerCleanup?.();
    record.mobileRoot = mobileRoot;
    mobileRoot.querySelectorAll('.t4s-menu-item-has-children').forEach((item) => {
      syncMobileItem(item, record, false);
    });

    record.mobileObserver = new MutationObserver((entries) => {
      const changedItems = new Set();
      entries.forEach((entry) => {
        if (entry.type === 'attributes' && entry.target.matches('.t4s-menu-item-has-children')) {
          changedItems.add(entry.target);
        }
      });
      changedItems.forEach((item) => syncMobileItem(item, record));
    });
    record.mobileObserver.observe(mobileRoot, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });

    const drawer = mobileRoot.closest('#t4s-menu-drawer');
    if (!drawer) return;

    const closeControl = drawer.nextElementSibling?.matches('.t4s-drawer-menu__close')
      ? drawer.nextElementSibling : null;
    const trigger = document.querySelector('[data-artbox-navigation] .t4s-push-menu-btn');

    const restoreScroll = () => {
      if (drawer.getAttribute('aria-hidden') !== 'true' || record.mobileOpenScrollY == null) return;
      window.scrollTo(0, record.mobileOpenScrollY);
    };
    const rememberScroll = (event) => {
      if (drawer.getAttribute('aria-hidden') !== 'false' && event.target.closest?.('[data-menu-drawer]')) {
        record.mobileOpenScrollY = window.scrollY;
      }
    };
    const syncDrawer = () => {
      const open = drawer.getAttribute('aria-hidden') === 'false';
      trigger?.setAttribute('aria-expanded', String(open));
      trigger?.setAttribute('aria-label', open ? 'Close menu' : 'Menu');
      if (open) {
        record.mobileWasOpen = true;
        clearMobileClosing();
      } else if (record.mobileWasOpen) {
        record.mobileWasOpen = false;
        markMobileClosing();
      }
      window.clearTimeout(record.mobileRestoreTimer);
      if (!open && record.mobileOpenScrollY != null) {
        restoreScroll();
        record.mobileRestoreTimer = window.setTimeout(() => {
          restoreScroll();
          record.mobileOpenScrollY = null;
          record.mobileRestoreTimer = null;
        }, 350);
      }
    };

    record.mobileDrawerObserver = new MutationObserver(syncDrawer);
    record.mobileDrawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
    drawer.addEventListener('transitionend', restoreScroll);
    document.addEventListener('click', rememberScroll, true);
    syncDrawer();
    record.mobileDrawerCleanup = () => {
      drawer.removeEventListener('transitionend', restoreScroll);
      document.removeEventListener('click', rememberScroll, true);
      window.clearTimeout(record.mobileRestoreTimer);
    };
  }

  function refresh(scope = document) {
    rootsWithin(scope).forEach((root) => {
      const record = records.get(root);
      if (record) {
        bindMobile(record);
        emitHeight(root, record);
      }
      else initialise(root.parentElement || document);
    });
  }

  function onMobileMenuTrigger(event) {
    const trigger = event.target.closest?.('[data-artbox-navigation] .t4s-push-menu-btn');
    if (!trigger || window.matchMedia('(min-width: 1025px)').matches) return;
    const root = trigger.closest('[data-artbox-navigation]');
    let record = records.get(root);
    if (!record) {
      initialise(document);
      record = records.get(root);
    } else bindMobile(record);
    const drawer = document.getElementById('t4s-menu-drawer');
    if (drawer?.getAttribute('aria-hidden') !== 'false') {
      if (record) record.mobileOpenScrollY = window.scrollY;
      return;
    }
    const closeControl = drawer.nextElementSibling?.matches('.t4s-drawer-menu__close')
      ? drawer.nextElementSibling : null;
    if (!closeControl) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    markMobileClosing();
    closeControl.click();
  }

  function onMobileMenuLink(event) {
    if (window.matchMedia('(min-width: 1025px)').matches) return;
    const link = event.target.closest?.('[data-artbox-mobile-menu] > li > a');
    const item = link?.parentElement;
    if (!item?.id.startsWith('item_artbox_mobile_')) return;

    // Kalles can restore older menu HTML from sessionStorage for 30 minutes.
    // Repair it before its delegated handler decides whether this tap expands.
    const desktopId = item.id.replace('item_artbox_mobile_', 'item_artbox_');
    const desktopLink = document.getElementById(desktopId)?.querySelector(':scope > a');
    if (desktopLink?.hasAttribute('href')) link.setAttribute('href', desktopLink.getAttribute('href'));
    item.classList.remove('t4s-only_icon_false');
  }

  function onMobileMenuIconClick(event) {
    if (window.matchMedia('(min-width: 1025px)').matches) return;
    // Keyboard/programmatic category activation must keep following its link.
    if (event.detail === 0 && !event.forwardedTouchEvent) return;
    const link = event.target.closest?.('[data-artbox-mobile-menu] > li > a');
    const item = link?.parentElement;
    if (!item?.id.startsWith('item_artbox_mobile_')) return;
    const icon = link.querySelector(':scope > .t4s-mb-nav__icon');
    if (!icon || icon.contains(event.target)) return;

    // Keep the original 12px plus, but accept nearby taps within a 44px area.
    const rect = icon.getBoundingClientRect();
    if (Math.abs(event.clientX - (rect.left + rect.width / 2)) > 22
      || Math.abs(event.clientY - (rect.top + rect.height / 2)) > 22) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const forwardedClick = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
      detail: event.detail,
      clientX: event.clientX,
      clientY: event.clientY,
    });
    // The body FastClick filter must allow this forwarding, then suppress the
    // original touch's trailing browser click before it reaches this handler.
    forwardedClick.forwardedTouchEvent = true;
    icon.dispatchEvent(forwardedClick);
  }

  function initialise(scope = document) {
    rootsWithin(scope).forEach((root) => {
      if (records.has(root)) {
        refresh(root);
        return;
      }

      const record = {
        root,
        height: -1,
        bottom: -1,
        hidden: null,
        resizeObserver: null,
        mutationObserver: null,
        activeItem: null,
        desktopObserver: null,
        desktopFrame: null,
        desktopCloseTimer: null,
        desktopCleanup: null,
        lastTrigger: null,
        overlayTimer: null,
        overlayFrame: null,
        surfaceTimer: null,
        panelTimers: new Map(),
        mobileAnimations: new WeakMap(),
        mobileObserver: null,
        mobileRoot: null,
        mobileDrawerObserver: null,
        mobileDrawerCleanup: null,
        mobileOpenScrollY: null,
        mobileRestoreTimer: null,
        mobileWasOpen: false,
      };

      records.set(root, record);

      if ('ResizeObserver' in window) {
        record.resizeObserver = new ResizeObserver(() => emitHeight(root, record));
        record.resizeObserver.observe(root);
      }

      record.mutationObserver = new MutationObserver(() => emitHeight(root, record));
      record.mutationObserver.observe(root.closest('.t4s-section-header') || root, {
        attributes: true,
        attributeFilter: ['class'],
      });

      bindDesktop(root, record);
      bindMobile(record);
      emitHeight(root, record);
    });
  }

  function destroy(scope = document) {
    rootsWithin(scope).forEach((root) => {
      const record = records.get(root);
      if (!record) return;
      record.resizeObserver?.disconnect();
      record.mutationObserver?.disconnect();
      record.desktopObserver?.disconnect();
      record.mobileObserver?.disconnect();
      record.mobileDrawerObserver?.disconnect();
      record.mobileDrawerCleanup?.();
      record.desktopCleanup?.();
      cancelAnimationFrame(record.desktopFrame);
      window.clearTimeout(record.overlayTimer);
      cancelAnimationFrame(record.overlayFrame);
      window.clearTimeout(record.surfaceTimer);
      record.panelTimers.forEach((timer) => window.clearTimeout(timer));
      records.delete(root);
    });
  }

  window.ArtboxNavigation = { initialise, destroy, refresh, closeDesktopMenu, reduceMotion };
  document.addEventListener('pointerdown', onMobileMenuLink, true);
  document.addEventListener('click', onMobileMenuLink, true);
  document.addEventListener('click', onMobileMenuIconClick);
  document.addEventListener('click', onMobileMenuTrigger, true);

  document.addEventListener('artbox:panel:change', (event) => {
    if (event.detail?.panel === 'none') return;
    rootsWithin(document).forEach((root) => closeDesktopMenu(root));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialise(event.target);
    initialise(document);
  });
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
